import { useEffect, useRef, useState } from 'react';
import { Form, Formik } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import EmojiPicker from 'emoji-picker-react';
import { useGetIdentity, useUploadMedia } from 'src/hooks';
import InputField from 'src/components/common/formFields/inputField/InputField';
import Button from 'src/components/button/Button';
import { ActionIcon } from 'src/components/common';
import { EmojiIcon, PlusIcon } from 'src/components/common/common.icons';
import { colors } from 'src/constants';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  TEditDirectMessageRequest,
  TFindCreateConversationRequest,
  TGetChatFilePresignedUrlActionRequest,
  TSendDirectMessageRequest
} from 'src/services/apiEndpoint.types.ts';
import {
  changeChatConversation,
  clearChatFiles,
  clearLocalChat,
  editDirectMessage,
  findCreateConversation,
  getChatFilePresignedUrl,
  sendDirectMessage
} from 'src/store/chat/chat.slice';
import { openPopup } from 'src/store/common/common.slice';
import './chatMessagesFooter.scss';
import { TChatMessageInitialValues, TChatMessagesFooterProps } from './chatMessagesFooter.types';
import FileCard from '../../fileCard/FileCard';
import { LuLink } from "react-icons/lu";
import { StorageUtils } from 'src/utils';
import { useNavigate } from 'react-router-dom';
import { socketService } from 'src/services/socket.api.service';

const ChatMessagesFooter = ({
  name,
  selectedUserId,
  selectedUserType,
  conversationId,
  editMessageSettings,
  updateMessageSettings,
  directMessageContext,
  localChat,
  setLocalChatData,
  lastMessageRef = null
}: TChatMessagesFooterProps) => {
  // Hooks
  const dispatch = useAppDispatch();
  const navigate = useNavigate()
  const { filesToUpload } = useAppSelector((store) => store.chat);
  const { getIdentities } = useGetIdentity();
  const { handleUploadMedia } = useUploadMedia();

  // State
  const [uploadedFiles, setUploadedFiles] = useState(filesToUpload)
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState<boolean>(false);
  const uploadFileRef = useRef<HTMLInputElement>(null);
  const [messageId, setMessageId] = useState<string>(uuidv4());

  // Constants
  const { authenticatedId, loggedInUserType } = getIdentities();
  const isEditMessageMode: boolean = editMessageSettings?.editMode;

  const initialValues: TChatMessageInitialValues = isEditMessageMode
    ? { message: editMessageSettings.message }
    : { message: '' };

  const turnEditModeOff = () => {
    updateMessageSettings({ editMode: false, messageId: '', message: '', files: [] });
  };

  const openImageFileManager = async () => {
    if (uploadFileRef.current) {
      uploadFileRef.current.click();
    }
  };

  const onMediaLoad = (
    data: string | ArrayBuffer | null,
    fileType: string,
    mediaType: string,
    name: string,
    size: number
  ) => {
    const fileId: string = uuidv4();

    const payload: TGetChatFilePresignedUrlActionRequest = {
      conversationId,
      messageId,
      id: fileId,
      fileName: name,
      fileData: data
    };
    dispatch(getChatFilePresignedUrl(payload));
  };

  const openEmptyMessagePopup = () => {
    dispatch(
      openPopup({
        popupMessage: 'You message is empty, Please type something before sending',
        popupType: 'error'
      })
    );
  };

  const handleSubmit = (
    values: TChatMessageInitialValues,
    { resetForm }: { resetForm: () => void }
  ) => {
    console.log("Incubator>>>>>>>>>>>>>>>>>>>")
    const { message } = values;

    if (selectedUserId && conversationId) {
      if (isEditMessageMode) {
        const payload: TEditDirectMessageRequest = {
          eventName:
            directMessageContext === 'directMessage'
              ? 'edit-conversation-message'
              : 'edit-idea-conversation-message',
          messageId: editMessageSettings?.messageId,
          message
        };
        if (message?.trim()) {
          dispatch(editDirectMessage(payload));
          turnEditModeOff();
          resetForm();
        } else {
          openEmptyMessagePopup();
        }
      } else if (!isEditMessageMode) {
        if (filesToUpload?.length > 0 || message?.trim()) {
          if (localChat?.id === conversationId) {
            const initiationPayload: TFindCreateConversationRequest = {
              senderUserId: authenticatedId,
              senderUserType: loggedInUserType,
              receiverUserId: localChat?.userId,
              receiverUserType: localChat?.userType,
              moduleType: localChat?.moduleType
            };
            setLocalChatData && setLocalChatData(null)
            dispatch(findCreateConversation(initiationPayload)).then((data) => {
              const newConversationId = data?.conversation?.id;
              if (newConversationId) {
                const payload: TSendDirectMessageRequest = {
                  userId: authenticatedId,
                  receiverId: localChat?.userId,
                  userType: loggedInUserType,
                  conversationId: newConversationId,
                  eventName:
                    directMessageContext === 'directMessage' ? 'conversation' : 'idea-conversation',
                  message,
                  messageId,
                  files: filesToUpload
                };
                const conversation = { ...localChat, conversationId: newConversationId, id: newConversationId }
                const path = `/chat?conversationId=${newConversationId}&userType=${localChat?.userType}&userId=${localChat?.userId}&prev=/chat`
                navigate(path)
                dispatch(changeChatConversation({ conversation }))

                dispatch(sendDirectMessage(payload));
                dispatch(clearLocalChat())
                resetForm();
                setMessageId(uuidv4());
                dispatch(clearChatFiles());
              }
            });
          } else {
            const payload: TSendDirectMessageRequest = {
              userId: authenticatedId,
              receiverId: selectedUserId,
              userType: loggedInUserType,
              conversationId,
              eventName:
                directMessageContext === 'directMessage' ? 'conversation' : 'idea-conversation',
              message,
              messageId,
              files: filesToUpload
            };
            dispatch(sendDirectMessage(payload)).then(() => {
              socketService.emit('get-unread-count', {
                currentUserId: authenticatedId,
                currentUserType: loggedInUserType,
                otherUserId: selectedUserId,
                otherUserType: selectedUserType
              });
              socketService.emit('get-last-count', {
                conversationId: conversationId,
                userType: selectedUserType,
                userId: selectedUserId
              });
            });
            // socketService.emit("count-unread-users", {
            //   userId: selectedUserId,
            //   userType: loggedInUserType
            // });
            if (socketService.connected) {
              // Send message
              // Then update unread count
              console.log("Socket connected!!!!!!!!!!!!!!!!!!", selectedUserId, selectedUserType);
              socketService.emit("count-unread-users", {
                userId: selectedUserId,
                userType: selectedUserType
              });
            } else {
              console.error("Socket not connected!");
              socketService.connect(); // Try reconnecting
            }
            // socketService.emit("chat-unread-count", {
            //   senderId: payload.userId,
            //   receiverId: selectedUserId
            // }, (response) => {
            //   console.log(response, 'responseData');
            // });
            resetForm();
            setMessageId(uuidv4());
            dispatch(clearChatFiles());
          }
          lastMessageRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        } else {
          openEmptyMessagePopup();
        }
      }
    }
  };
  useEffect(() => {
    setUploadedFiles(filesToUpload)
  }, [filesToUpload])
  useEffect(() => {
    if (editMessageSettings?.files?.length > 0 && editMessageSettings?.editMode) {
      setUploadedFiles(editMessageSettings?.files)
    }
  }, [editMessageSettings?.files]);

  return (
    <div className="dm-chat-messages-footer">
      <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
        {({ setFieldValue, values }) => {
          return (
            <Form className="dm-chat-messages-footer__form">
              <div className="dm-chat-messages-footer__form__row">
                <InputField
                  id="message"
                  placeholder={`Message to ${name}`}
                  value={values?.message}
                />
                <div>

                  <div className="dm-chat-messages-footer__form__row__actions">
                    <div className="dm-chat-messages-footer__form__row__actions">
                      <input
                        type="file"
                        hidden
                        data-testid="inputUploadImage"
                        ref={uploadFileRef}
                        accept=".bmp,.jpg,.jpeg,.png,.pdf,.mp4,.webm, .docx, .doc, .xls, .xlsx, .mp3, .flv, .mkv, .webp"
                        className="select-post-image"
                        multiple
                        onChange={(e) => {
                          handleUploadMedia({ e, onLoad: onMediaLoad, mediaType: 'program' });
                        }}
                      />
                      {!editMessageSettings?.editMode && (
                        <div className='emojiLink_icons' onClick={openImageFileManager}>
                          <LuLink color={colors?.neutral?.[700]} />
                        </div>
                      )}
                      <div className='emojiLink_icons' onClick={() => setIsEmojiPickerOpen((prev) => !prev)}>
                        <EmojiIcon />
                      </div>
                    </div>{' '}
                    <EmojiPicker
                      open={isEmojiPickerOpen}
                      onEmojiClick={(e) => {
                        setFieldValue('message', values.message + e.emoji);
                        setIsEmojiPickerOpen(false);
                      }}
                    />

                    {editMessageSettings.editMode && (
                      <Button
                        size="small"
                        variant="secondary"
                        type="button"
                        className="dm-chat-messages-footer__form__row__actions__cancel"
                        onClick={turnEditModeOff}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button size="small" variant="primary" type="submit">
                      {editMessageSettings.editMode ? 'Save' : 'Send'}
                    </Button>
                  </div>
                </div>
              </div>
              {uploadedFiles?.length > 0 && !editMessageSettings?.editMode && (
                <div className="dm-chat-messages-footer__form__row__files">
                  {uploadedFiles.map(({ id, fileName }) => (
                    <FileCard
                      key={id}
                      id={id}
                      fileName={fileName}
                      showDownloadButton={false}
                      showDeleteButton={true}
                    />
                  ))}
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default ChatMessagesFooter;
